// What a multi-config bundle child needs from the customer, and whether it has
// it. Shared by MultiConfig (required counts + step transitions),
// MultiConfigProductItem (the ✅ marker and whether the tile is interactive) and
// MultiConfigProductItemCTA (Configure / Choose Photo / Edit Selection).
//
// All four used to answer this independently, and all four assumed every child
// with a positive `backgrounds_count` needs a background picked. A digital
// download that resolves its own background (all_backgrounds / no_background)
// reports a positive count but must never be asked for one, so it was routed into
// a picker whose choice is discarded — or charged as an upgrade — and then left
// permanently incomplete: no ✅, a CTA still reading "Choose Photo", and a
// required count that "Done, continue" could never reach.
import { productNeedsBackgroundSelection } from '../productConfigPredicates';
import { productPosesCount } from '../imageRecords';

// Child products arrive straight from the serialized price list and the Redux
// store is untyped, so this matches the `Product = any` used by imageRecords
// rather than inventing a shape the API does not guarantee.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type BundleChild = any;

// One entry of MultiConfig's local `products` state, keyed by child id.
interface ConfiguredChild {
  product: BundleChild;
  pose?: unknown;
  background?: unknown;
}

interface FlowMode {
  isPrepay: boolean;
}

// Does this child have anything for the customer to pick at all?
//
// Prepay has no pose step, so there a child is only configurable when it needs a
// background PICKED. Non-configurable children still render in the grid — the
// customer should see what the bundle includes — but get no CTA and no click
// handler.
export const childIsConfigurable = (
  child: BundleChild,
  { isPrepay }: FlowMode,
): boolean =>
  isPrepay
    ? productNeedsBackgroundSelection(child)
    : productPosesCount(child) > 0;

// Has everything this child actually needs been picked?
//
// A pose, except on prepay where none is offered, plus a background only if the
// child needs one selected.
export const childIsConfigured = (
  { product, pose, background }: ConfiguredChild,
  { isPrepay }: FlowMode,
): boolean =>
  (isPrepay || Boolean(pose)) &&
  (!productNeedsBackgroundSelection(product) || Boolean(background));
